<i18n>
{
	"en": {
		"clearSelection": "Clear selection",
		"close": "Close",
		"goToToday": "Go to today",
		"selectDecade": "Select Decade",
		"selectMonth": "Select Month",
		"selectYear": "Select Year",
		"selectTime": "Select time"
	},
	"no": {
		"clearSelection": "Tøm valg",
		"close": "Lukk",
		"goToToday": "Gå til i dag",
		"selectDecade": "Velg Tiår",
		"selectMonth": "Velg Måned",
		"selectYear": "Velg År",
		"selectTime": "Velg tid"
	}
}
</i18n>
<template>
	<t-popover
		:showPopover="popoverIsVisible"
		:marginY="6"
		:style="getStyle"
		position="bottom"
		:width="260"
		v-bind="$attrs"
	>
		<t-input-wrapper
			slot="target"
			:style="useMinWidth ? { width: $i18n.locale === 'en' ? '100px' : '80px' } : {}"
			:label="label"
			:description="description"
			:hint="hint"
			:isInvalid="isInvalid"
			:validationMessage="validationMessage"
			:warningMessage="warningMessage"
		>
			<t-text-input-button
				:placeholder="placeholder ? placeholder : $t('selectTime')"
				:value="internalValue"
				:size="size"
				:disabled="disabled"
				:disableTabbing="disableTabbing"
				:disableButtonTabbing="disableButtonTabbing"
				icon="time"
				iconSize="sm"
				width="100%"
				@change="onTextInputChanged"
				@button:click="popoverToggle"
				@keydown.enter="onEnterDown"
				@keydown.tab="onTabDown"
				@button:blur="popoverSetHidden"
				@keydown="onButtonKeyDown"
				@focus="$emit('focus', $event) && $event.target.select()"
			/>
		</t-input-wrapper>
		<date-picker
			id="time-input"
			ref="timePicker"
			:value="internalValue"
			:disabled="disabled"
			:wrap="true"
			:config="options"
			style="width:100%;"
			@input="onTextInputChanged"
			@keydown.enter="onEnterDown"
			@keydown.tab="onTabDown"
		/>
	</t-popover>
</template>


<script>
import DatePicker from 'vue-bootstrap-datetimepicker';
//import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import './scss/_date-picker.scss';
import TTextInputButton from '@/components/evergreen/inputs/TTextInputButton';
import TInputWrapper from '@/components/evergreen/inputs/TInputWrapper';
import TPopover from '@/components/evergreen/overlays/TPopover';
import formMixin from './formMixin';
import inputWrapperMixin from './inputWrapperMixin';
import popoverMixin from './popoverMixin';
import moment from 'moment';
export default {
	name: 'TTimePicker',
	components: {
		DatePicker,
		TTextInputButton,
		TInputWrapper,
		TPopover
	},
	mixins: [ formMixin, inputWrapperMixin, popoverMixin ],
	inheritAttrs: false,
	props: {
		appearance: String,
		intent: String,
		disableTabbing: {
			type: Boolean,
			default: false
		},
		disableButtonTabbing: {
			type: Boolean,
			default: false
		},
		disableTimeoutForEnterEvent: {
			type: Boolean,
			default: false
		},
		useMinWidth: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return	{
			internalValue: undefined,
		};
	},
	computed: {
		options() {
			return {
				minDate: false,
				maxDate: false,
				allowInputToggle: true,
				format: 'LT',
				useCurrent: false,
				debug: false,
				locale: this.getLocale(),
				inline: true,
				icons: {
					previous: 'fas fa-chevron-left fa-sm',
					next: 'fas fa-chevron-right fa-sm',
					close: 'fas fa-times fa-sm',
					clear: 'fas fa-trash-alt fa-sm',
					today: 'fas fa-calendar-check fa-sm'
				},
				tooltips: {
					today: this.$t('goToToday'),
					clear: this.$t('clearSelection'),
					close: this.$t('close'),
					selectMonth: this.$t('selectMonth'),
					selectYear: this.$t('selectYear'),
					selectDecade: this.$t('selectDecade')
				},
			};
		},
		getStyle() {
			return `width: ${this.width};`;
		},
		isValid() {
			return this.isValid ? 'is-invalid' : '';
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.internalValue = newValue;
			}
		}
	},
	methods: {
		onEnterDown(event) {
			/**
			 * Fired when Enter key is pressed when date picker is focused. disableTimeoutForEnterEvent is needed to
			 * use stopPropagation in a parent component.
			 * @event enter
			 */
			if (this.disableTimeoutForEnterEvent) {
				this.$emit('enter', event);
			} else {
				setTimeout(() => this.$emit('enter', event), 0);
			}
		},
		onTabDown(event) {
			this.$emit('tab', event);
		},
		onTextInputChanged(value) {
			let date = moment(value, 'LT');
			if (date.isValid()) {
				this.onInputChanged(date.format('LT'));
			} else {
				this.onInputChanged(value);
			}
		},
		onInputChanged(value) {
			/**
			 * Fired immediately when input value is changed.
			 * @event input
			 * @type {object}
			 */
			if (this.internalValue === value) {
				return;
			}
			this.internalValue = value;

			this.$emit('input', value);
		},
		setNow() {
			let newValue = moment();
			if (newValue.minute() >= 30) {
				newValue = newValue.add(1, 'h').minute(0);
			} else {
				newValue = newValue.minute(0);
			}
			this.onTextInputChanged(newValue);
		},
		getLocale() {
			let lang = localStorage.getItem('locale');
			if (lang === 'no') {
				lang = 'nb';
			}
			return lang;
		},
		focus() {
			this.$refs.timePicker.$el.focus();
		},
		onButtonKeyDown(event) {
			let hotkeys = ['KeyT', 'KeyN', 'ArrowUp', 'ArrowDown'];
			if (hotkeys.includes(event.code)) {
				event.preventDefault();
				this[`on${event.code}`]();
			}
			this.$emit('button:keydown', event);
		},
		onKeyT() {
			this.setNow();
		},
		onKeyN() {
			this.setNow();
		},
		onArrowUp() {
			let newValue = moment(this.internalValue, this.options.format).add(1, 'h');
			this.onTextInputChanged(newValue);
		},
		onArrowDown() {
			let newValue = moment(this.internalValue, this.options.format).add(-1, 'h');
			this.onTextInputChanged(newValue);
		}
	},

};
</script>

<style>
</style>
